/*
 * cb-gallery-guard.js v2.2 — product gallery assist + the ONE product-photo monitor (Sep 2026)
 *
 * One rule for "the photo the shopper is looking at": the gallery's own active photo
 * (per-gallery adapter below) that is >= 50% on screen while the tab is visible.
 * Never HTML order. New PDP layout = add one adapter. cb_monitor_blind flags broken adapters.
 *
 * SPEED BUDGET (low-end Android, slow 3G):
 *  - Loaded with defer on product pages only; ~4 KB gzipped once minified; no render blocking.
 *  - Never competes with the first photo: neighbour slides are preloaded only AFTER the photo on
 *    screen has finished loading, in idle time; on Save-Data / 2G only the next 1 slide.
 *  - No work when nothing is on screen: the check loop stops and restarts on scroll-in / tab return.
 *  - While a photo is loading: light checks every 250 ms (image flags, no layout).
 *    Once it shows: only an overlap check once a second (1 hit-test), no style reads.
 *  - DOM observer watches the gallery elements only, not the whole page.
 *  - Reads only, no layout writes (except the one-off AOS reveal fix).
 *
 * Events (Clarity event + session tag, and GA4):
 *    cb_gallery_blank     current photo on screen but blank > BLANK_MS (value = blank ms)
 *    cb_gallery_stuck     current photo on screen, still blank after STUCK_MS
 *    cb_gallery_covered   unexpected element over the current photo > COVER_MS
 *    cb_exit_img_pending  shopper left / switched app while the current photo was still blank
 *    cb_monitor_blind     self-check: product page where no gallery / no current photo was found
 *    cb_gallery_summary   GA4 only, deltas on every hide + pagehide (summary_part 1,2..).
 *                         Pages = count where summary_part = 1.
 *    Reasons: loading (not downloaded yet), broken (failed / empty), invisible (downloaded, transparent).
 *
 * v2.2 (13 Oct 2026, Claude) - MEASUREMENT-FIX, so the numbers can be trusted for decisions.
 *   Evidence (GA4, 26 Sep - 12 Oct): about half of the "left with a blank photo" sessions were
 *   old-format events from the retired cb-monitor.js check still running in cached browsers; ~160 of
 *   324 "stuck after 5 s" sessions were bots (desktop Windows Chrome, Singapore / US / unknown city,
 *   direct, 1280x1200 screen); "invisible" also counted the theme's 0.5 s fade-in while a photo was
 *   already appearing.
 *   Changes (only these cb_* events change - no page_view / ecommerce / Ads / Meta event is touched):
 *   - every GA4 event carries cb_v = '2.2' (events without it = older script);
 *   - automated browsers (navigator.webdriver, headless / Lighthouse / crawler user agents) send
 *     nothing - the page itself behaves exactly the same for them;
 *   - cb_phase on blank / stuck / exit events: opening (photo the page opened on), colour (after a
 *     colour pick) or swipe; cb_blank_bucket on exit events (<1s, 1-3s, 3-5s, 5s+);
 *   - a photo whose fade-in animation is running counts as showing, not "invisible";
 *   - cb_monitor_blind "opening_mismatch" / "preload_mismatch": the slide theme.js opened on, or the
 *     photo preloaded in <head>, differs from <meta name="cb-pdp-opening"> (lcp-preload.liquid).
 *   To report on the new params, register them in GA4 (Admin > Custom definitions): event-scoped
 *   dimensions cb_v, cb_phase, cb_blank_bucket and custom metric cb_ms (milliseconds).
 *   Undo: restore v2.1 from the 13Oct - 2pm theme.
 */
(function () {
  'use strict';
  if (window.__cbGalleryGuard || !/\/products\//.test(location.pathname)) return;
  window.__cbGalleryGuard = true;

  var BLANK_MS = 1000, STUCK_MS = 5000, COVER_MS = 3000;
  var REVEAL_AFTER_MS = 800, RETRY_AFTER_MS = 1500, BLIND_CHECK_MS = 4000;
  var FAST_MS = 250;   // loop speed while a photo on screen is still blank
  var SLOW_MS = 1000;  // loop speed once photos on screen are showing (overlap check only)
  var MAX_PER_EVENT = 5;
  // TRACKING-FIX (13 Oct 2026, Claude): GA4 G-80LLZ17V1J only (was also G-8899R3KRW0, a 2nd
  // property that got only these cb_* events - same data twice, and an extra Google script).
  // Undo: add 'G-8899R3KRW0' back to this list.
  var GA4_IDS = ['G-80LLZ17V1J'];
  var EXPECTED_OVERLAY = '.pswp, .drawer, .modal, [role="dialog"], #CartDrawer';
  var VERSION = '2.2';

  /* ---------- Gallery adapters: the only place that knows theme markup ----------
     current() must be cheap (class checks only): the IntersectionObserver already
     guarantees the photo is laid out and on screen. */
  var GALLERIES = [
    { // Impulse main-product gallery (Flickity, colour image sets via data-group)
      name: 'impulse',
      root: '[data-product-photos]',
      imgs: '.product-main-slide img',
      current: function (img) {
        var s = img.closest('.product-main-slide');
        return !!s && !s.classList.contains('hide');
      }
    },
    { // satin-pdp-hero (fitted sheet): stacked colour photos, active one has .is-active
      name: 'satin_hero',
      root: '[data-sp-stage]',
      imgs: 'img[data-sp-color-image]',
      current: function (img) { return img.classList.contains('is-active'); }
    }
  ];

  var w = window, d = document;
  var now = function () { return Math.round(performance.now()); };
  w.clarity = w.clarity || function () { (w.clarity.q = w.clarity.q || []).push(arguments); };

  var ua = navigator.userAgent || '';
  var browserType = /Instagram/i.test(ua) ? 'instagram_app'
    : /FBAN|FBAV|FB_IAB/i.test(ua) ? 'facebook_app'
    : /\bwv\b|WebView/i.test(ua) ? 'android_webview' : 'browser';
  // v2.2: robots / speed-test tools must not count as shoppers (they still get the same page).
  var isBot = !!navigator.webdriver ||
    /HeadlessChrome|Lighthouse|PTST|GTmetrix|AdsBot|[a-z]bot\/|\bbot\b|crawler|spider|facebookexternalhit|Google-InspectionTool/i.test(ua);
  function network() {
    var c = navigator.connection;
    return (c && c.effectiveType) || 'na';
  }
  function slowLink() {
    var c = navigator.connection;
    return !!c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''));
  }
  var idle = w.requestIdleCallback
    ? function (fn) { w.requestIdleCallback(fn, { timeout: 2000 }); }
    : function (fn) { setTimeout(fn, 200); };

  var stats = {
    seen: 0, blank: 0, stuck: 0, covered: 0, maxBlank: 0, totalBlank: 0,
    preload: 0, reveal: 0, retry: 0, retryOk: 0
  };
  var counts = {};

  function report(name, detail, ms, extra) {
    try {
      if (isBot) return;
      counts[name] = (counts[name] || 0) + 1;
      if (counts[name] > MAX_PER_EVENT) return;
      detail = detail == null ? '' : String(detail).slice(0, 250);
      w.clarity('event', name);
      if (detail) w.clarity('set', name, detail);
      if (ms != null) w.clarity('set', name + '_ms', String(ms));
      w.clarity('set', 'cb_browser', browserType);
      if (extra && extra.cb_phase) w.clarity('set', name + '_phase', extra.cb_phase);
      if (typeof w.gtag === 'function') {
        var params = {
          send_to: GA4_IDS,
          cb_v: VERSION,
          cb_detail: detail,
          cb_ms: ms == null ? undefined : ms,
          cb_browser: browserType,
          cb_page: location.pathname.slice(0, 100)
        };
        if (extra) Object.keys(extra).forEach(function (k) { params[k] = extra[k]; });
        w.gtag('event', name, params);
      }
    } catch (e) { /* never break the page */ }
  }

  /* ---------- helpers ---------- */
  var adapterOf = new WeakMap(); // img -> { g: adapter, root: element }
  function info(img) { return adapterOf.get(img); }
  function isCurrent(img) {
    var a = info(img);
    try { return !!a && img.isConnected && a.g.current(img); } catch (e) { return false; }
  }
  function fileOf(img) {
    var src = img.currentSrc || img.src || img.getAttribute('src') || '';
    return src.split('/').pop().split('?')[0].slice(0, 90);
  }
  function slideOf(img) { return img.closest('.product-main-slide'); }
  function label(img) {
    var a = info(img), s = slideOf(img);
    var where = s ? ' #' + (s.getAttribute('data-index') || '?') + ' ' + (s.getAttribute('data-group') || '')
      : (img.getAttribute('data-sp-color-image') ? ' ' + img.getAttribute('data-sp-color-image') : '');
    return (a ? a.g.name : '?') + ' ' + fileOf(img) + where;
  }
  function loaded(img) { return img.complete && img.naturalWidth > 0; }

  // Opacity as the shopper sees it, up to the gallery root. Only called for a downloaded photo
  // that is not yet confirmed visible (usually 1-2 calls per photo).
  function opacityOf(img) {
    var a = info(img), stop = a ? a.root : null, o = 1;
    for (var n = img; n && n !== d.body; n = n.parentElement) {
      var cs = getComputedStyle(n);
      if (cs.visibility === 'hidden' || cs.display === 'none') return 0;
      o *= parseFloat(cs.opacity || '1');
      if (n === stop) break;
    }
    return o;
  }
  // v2.2: a running fade-in (theme "image-fade-in") means the photo is appearing, not stuck.
  function fadingIn(img) {
    try {
      return !!img.getAnimations && img.getAnimations().some(function (an) { return an.playState === 'running'; });
    } catch (e) { return false; }
  }
  function blankReason(img) {
    if (!img.complete) return 'loading';          // flag read, no layout
    if (img.naturalWidth === 0) return 'broken';  // flag read, no layout
    if (opacityOf(img) < 0.3 && !fadingIn(img)) return 'invisible';
    return null;
  }

  /* ---------- v2.2: which moment is this photo? (opening / colour / swipe) ---------- */
  var openingMeta = d.querySelector('meta[name="cb-pdp-opening"]');
  var openingIndex = openingMeta ? openingMeta.getAttribute('content') : null;
  var colourPicked = false, touchedGallery = false;
  d.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('.variant-input-wrap, [data-sp-color], [data-sp-stage]')) colourPicked = true;
  }, true);
  d.addEventListener('pointerdown', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('[data-product-photos], .product__thumbs, [data-sp-stage]')) touchedGallery = true;
  }, true);
  function phaseOf(img) {
    var s = slideOf(img);
    if (colourPicked) return 'colour';
    if (s) {
      if (openingIndex != null) return s.getAttribute('data-index') === openingIndex && !touchedGallery ? 'opening' : 'swipe';
      return touchedGallery ? 'swipe' : 'opening';
    }
    return 'opening'; // satin_hero: only colour picks change the photo
  }
  function bucket(ms) {
    return ms < 1000 ? '<1s' : ms < 3000 ? '1-3s' : ms < 5000 ? '3-5s' : '5s+';
  }
  function coveredBy(img) {
    var a = info(img);
    if (!a) return null;
    var r = img.getBoundingClientRect();
    var x = r.left + r.width / 2, y = r.top + r.height / 2;
    if (x < 0 || y < 0 || x > innerWidth || y > innerHeight) return null;
    var top = d.elementFromPoint(x, y);
    var box = a.root.closest('[data-product-images]') || a.root;
    if (!top || box.contains(top) || (top.closest && top.closest(EXPECTED_OVERLAY))) return null;
    var cls = typeof top.className === 'string' ? top.className.trim().split(/\s+/).slice(0, 2).join('.') : '';
    return top.tagName.toLowerCase() + (top.id ? '#' + top.id : '') + (cls ? '.' + cls : '');
  }

  /* ---------------- A. ASSIST (Impulse slides) ---------------- */
  function eager(img, high) {
    try {
      if (img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
        if (!loaded(img)) stats.preload++;
      }
      if (high && 'fetchPriority' in img && img.fetchPriority !== 'high') img.fetchPriority = 'high';
    } catch (e) {}
  }
  function neighbours(slide, ahead) {
    var out = [], n, k = 0;
    for (n = slide.nextElementSibling; n && k < ahead; n = n.nextElementSibling) {
      if (n.classList.contains('product-main-slide') && !n.classList.contains('hide')) { out.push(n); k++; }
    }
    if (ahead > 1) {
      for (n = slide.previousElementSibling; n; n = n.previousElementSibling) {
        if (n.classList.contains('product-main-slide') && !n.classList.contains('hide')) { out.push(n); break; }
      }
    }
    return out;
  }
  // Called only once the on-screen photo has finished showing, so preloads never
  // share bandwidth with the photo the shopper is waiting for.
  function preloadNeighbours(img) {
    if (img.__cbPreloaded) return;
    img.__cbPreloaded = true;
    var slide = slideOf(img);
    if (!slide) return;
    idle(function () {
      neighbours(slide, slowLink() ? 1 : 2).forEach(function (s) {
        s.querySelectorAll('img').forEach(function (i) { eager(i, false); });
      });
    });
  }
  function reveal(img) {
    var changed = false;
    var mark = function (el) {
      if (el && el.hasAttribute('data-aos') && !el.classList.contains('aos-animate')) {
        el.classList.add('aos-animate');
        changed = true;
      }
    };
    var slide = slideOf(img);
    if (slide) {
      mark(slide);
      slide.querySelectorAll('[data-aos]').forEach(mark);
    }
    mark(img.closest('.product__main-photos'));
    if (changed) stats.reveal++;
    return changed;
  }
  d.addEventListener('error', function (e) {
    var img = e.target;
    if (!img || img.tagName !== 'IMG' || img.__cbRetried || !info(img)) return;
    img.__cbRetried = true;
    var failed = img.currentSrc || img.src;
    if (!failed) return;
    setTimeout(function () {
      if (!img.isConnected) return;
      stats.retry++;
      img.addEventListener('load', function () { stats.retryOk++; }, { once: true });
      img.removeAttribute('srcset');
      img.src = failed + (failed.indexOf('?') < 0 ? '?' : '&') + 'cbretry=1';
    }, RETRY_AFTER_MS);
  }, true);

  /* ---------------- B. MONITOR ---------------- */
  var seenImgs = new WeakSet();
  var onScreen = new Set();   // gallery photos >= 50% in viewport (any colour)
  var watching = new Map();   // current + on-screen photo -> watch state

  function finishBlank(img, st, ms) {
    if (ms <= BLANK_MS) return;
    stats.blank++;
    stats.totalBlank += ms;
    if (ms > stats.maxBlank) stats.maxBlank = ms;
    report('cb_gallery_blank', (st.lastReason || 'loading') + ' ' + network() + ' ' + label(img), ms, { cb_phase: st.phase });
  }

  function tick(img, st, t) {
    if (!st.done) {
      var r = blankReason(img);
      if (r) {
        st.lastReason = r;
        var elapsed = t - st.start;
        if (r === 'invisible' && !st.revealTried && elapsed >= REVEAL_AFTER_MS) {
          st.revealTried = true;
          reveal(img);
        }
        if (!st.stuckReported && elapsed >= STUCK_MS) {
          st.stuckReported = true;
          stats.stuck++;
          report('cb_gallery_stuck', r + ' ' + network() + ' ' + label(img), STUCK_MS, { cb_phase: st.phase });
        }
        return;
      }
      st.done = true;
      finishBlank(img, st, t - st.start);
      preloadNeighbours(img);
      return;
    }
    // Showing: overlap check at most once a second, stops once reported.
    if (st.coverReported || t - st.lastCoverCheck < SLOW_MS) return;
    st.lastCoverCheck = t;
    var cover = coveredBy(img);
    if (!cover) { st.coverSince = null; return; }
    if (st.coverSince == null) st.coverSince = t;
    if (t - st.coverSince >= COVER_MS) {
      st.coverReported = true;
      stats.covered++;
      report('cb_gallery_covered', cover + ' over ' + label(img), t - st.coverSince);
    }
  }

  function startWatch(img) {
    if (watching.has(img)) return;
    if (!seenImgs.has(img)) { seenImgs.add(img); stats.seen++; }
    // The photo the shopper is waiting for gets top download priority.
    if (!loaded(img)) eager(img, true);
    var t = now();
    var st = { start: t, done: false, lastReason: null, revealTried: false, stuckReported: false,
      coverSince: null, coverReported: false, lastCoverCheck: t, phase: phaseOf(img) };
    watching.set(img, st);
    tick(img, st, t);
  }
  // countIt=false when the page went to the background: that time is not the shopper's view.
  function stopWatch(img, countIt) {
    var st = watching.get(img);
    if (!st) return;
    watching.delete(img);
    if (!st.done && countIt) finishBlank(img, st, now() - st.start);
  }

  // One loop, self-scheduling: fast while something is blank, slow once all is showing,
  // stopped when no gallery photo is on screen or the tab is hidden.
  var timer = null;
  function loop() {
    timer = null;
    if (d.visibilityState !== 'visible') return;
    var t = now(), anyBlank = false;
    onScreen.forEach(function (img) {
      if (!img.isConnected) { onScreen.delete(img); stopWatch(img, true); return; }
      if (isCurrent(img)) startWatch(img); else stopWatch(img, true); // swapped away while blank = seen blank
    });
    watching.forEach(function (st, img) {
      if (!onScreen.has(img)) { stopWatch(img, true); return; }
      tick(img, st, t);
      if (!st.done) anyBlank = true;
    });
    if (onScreen.size) timer = setTimeout(loop, anyBlank ? FAST_MS : SLOW_MS);
  }
  function kick() {
    if (timer) { clearTimeout(timer); timer = null; }
    loop();
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (en.isIntersecting && en.intersectionRatio >= 0.5) onScreen.add(en.target);
      else { onScreen.delete(en.target); stopWatch(en.target, true); }
    });
    kick();
  }, { threshold: [0, 0.5] });

  var watchedRoots = new WeakSet();
  var mo = new MutationObserver(function (muts) {
    muts.forEach(function (m) { if (m.addedNodes.length) scanRoot(m.target.closest ? m.target : null); });
  });
  function scanRoot(node) {
    if (!node) return;
    GALLERIES.forEach(function (g) {
      var root = node.closest(g.root);
      if (!root) return;
      root.querySelectorAll(g.imgs).forEach(function (img) {
        if (adapterOf.has(img)) return;
        adapterOf.set(img, { g: g, root: root });
        io.observe(img);
      });
    });
  }
  function attach() {
    GALLERIES.forEach(function (g) {
      d.querySelectorAll(g.root).forEach(function (root) {
        if (watchedRoots.has(root)) return;
        watchedRoots.add(root);
        root.querySelectorAll(g.imgs).forEach(function (img) {
          adapterOf.set(img, { g: g, root: root });
          io.observe(img);
        });
        // Colour sets fetched later (pdp-gallery-sets.liquid) are added inside the root.
        mo.observe(root, { childList: true, subtree: true });
      });
    });
  }

  /* ---------- exit with a blank photo ---------- */
  var exitReported = false;
  function exitCheck() {
    if (exitReported) return;
    watching.forEach(function (st, img) {
      if (exitReported || st.done) return;
      exitReported = true;
      var ms = now() - st.start;
      report('cb_exit_img_pending', (st.lastReason || 'loading') + ' ' + network() + ' ' + label(img), ms,
        { cb_phase: st.phase, cb_blank_bucket: bucket(ms) });
    });
  }

  /* ---------- self-check: is the monitor actually seeing the gallery? ---------- */
  var everHidden = d.visibilityState !== 'visible';
  // v2.2: the gallery must open on the slide the server chose (cb-pdp-opening-media.liquid), and the
  // <head> preload must be that slide's photo. A mismatch means a theme / snippet change broke the
  // shared rule - the shopper may see a blank or wrong photo first.
  function openingCheck() {
    if (openingIndex == null || colourPicked || touchedGallery) return;
    var root = d.querySelector('[data-product-photos]');
    var sel = root && root.querySelector('.product-main-slide.is-selected');
    if (!sel) return;
    var shown = sel.getAttribute('data-index');
    if (shown !== openingIndex) {
      report('cb_monitor_blind', 'opening_mismatch server=' + openingIndex + ' shown=' + shown);
      return;
    }
    var pre = d.querySelector('link[rel="preload"][as="image"][imagesrcset]');
    var img = sel.querySelector('img');
    if (pre && img && w.matchMedia && w.matchMedia(pre.media || 'all').matches) {
      var file = function (u) { return String(u || '').split('/').pop().split('?')[0]; };
      var want = file(pre.getAttribute('href')), got = file(img.currentSrc || img.getAttribute('src'));
      if (want && got && want !== got) report('cb_monitor_blind', 'preload_mismatch preload=' + want.slice(0, 60) + ' shown=' + got.slice(0, 60));
    }
  }
  function blindCheck() {
    openingCheck();
    if (everHidden || w.scrollY > 200) return; // shopper left or scrolled past the photo
    var anyRoot = GALLERIES.some(function (g) { return !!d.querySelector(g.root); });
    if (!anyRoot) { report('cb_monitor_blind', 'no_gallery'); return; }
    if (stats.seen) return;
    var found = 0;
    d.querySelectorAll(GALLERIES.map(function (g) { return g.root + ' ' + g.imgs; }).join(',')).forEach(function (i) {
      if (isCurrent(i)) found++;
    });
    report('cb_monitor_blind', found ? 'current_not_on_screen' : 'no_current_photo');
  }

  /* ---------- GA4 summary as deltas ---------- */
  var sent = {}, part = 0;
  function sendSummary() {
    try {
      if (isBot || !stats.seen || typeof w.gtag !== 'function') return;
      var keys = Object.keys(stats), delta = {}, changed = false;
      keys.forEach(function (k) {
        delta[k] = stats[k] - (sent[k] || 0);
        if (delta[k]) changed = true;
      });
      if (!changed) return;
      keys.forEach(function (k) { sent[k] = stats[k]; });
      part++;
      w.gtag('event', 'cb_gallery_summary', {
        send_to: GA4_IDS,
        transport_type: 'beacon',
        cb_v: VERSION,
        summary_part: part,
        cb_page: location.pathname.slice(0, 100),
        cb_browser: browserType,
        cb_network: network(),
        gallery_seen: delta.seen,
        gallery_blank: delta.blank,
        gallery_stuck: delta.stuck,
        gallery_covered: delta.covered,
        gallery_max_blank_ms: stats.maxBlank, // running max for the page, not a delta
        gallery_total_blank_ms: delta.totalBlank,
        gallery_preloads: delta.preload,
        gallery_reveals: delta.reveal,
        gallery_retries: delta.retry,
        gallery_retries_ok: delta.retryOk
      });
    } catch (e) {}
  }

  function onHide() {
    everHidden = true;
    if (timer) { clearTimeout(timer); timer = null; }
    exitCheck();
    Array.from(watching.keys()).forEach(function (img) { stopWatch(img, false); });
    sendSummary(); // in-app browsers often skip pagehide, so every hide is a send point
  }
  d.addEventListener('visibilitychange', function () {
    if (d.visibilityState === 'hidden') onHide(); else kick();
  });
  w.addEventListener('pagehide', onHide);

  function start() {
    attach();
    setTimeout(blindCheck, BLIND_CHECK_MS);
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})();
